"use client";

import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import DataTable from "@/components/ui/DataTable";
import DrawerPanel from "@/components/ui/DrawerPanel";
import Input from "@/components/form/input/InputField";
import Label from "@/components/form/Label";
import SearchableSelect from "@/components/ui/SearchableSelect";
import Pagination from "@/components/tables/Pagination";
import { Modal } from "@/components/ui/modal/index";
import Radio from "@/components/form/input/Radio";
import { handleApiError } from "@/lib/api";
import { rmcClient } from "@/lib/rmcClient";
import { roundMoney } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

const PAGE_SIZE = 20;

const num = z.preprocess(
  (v) => (v === "" || v === undefined || v === null ? NaN : Number(v)),
  z.number()
);

const quotationSchema = z.object({
  party_id: z.coerce.number({ invalid_type_error: "Party required" }).positive("Party required"),
  date: z.preprocess(
    (v) => (v === undefined || v === null ? "" : String(v)),
    z.string().min(1, "Date required")
  ),
  valid_until: z.preprocess(
    (v) => (v === undefined || v === null ? "" : String(v)),
    z.string().min(1, "Valid until required")
  ),
  is_active: z.boolean().default(true),
  items: z
    .array(
      z.object({
        material_id: z.coerce.number({ invalid_type_error: "Material required" }).positive("Material required"),
        quarry_id: z.coerce.number({ invalid_type_error: "Quarry required" }).positive("Quarry required"),
        rate: num.pipe(z.number().positive("Enter a valid rate")),
      })
    )
    .min(1, "Add at least one line"),
});

function partyLabel(row, parties) {
  const id = row?.party_id ?? row?.party?.id;
  return row?.party?.name ?? parties.find((p) => p.id === id)?.name ?? "—";
}

/** Type-to-filter + click-to-select combobox for purchase parties (SearchableSelect). */
function PurchasePartyCombo({
  label,
  value,
  onChange,
  parties = [],
  placeholder = "Select party…",
  allowAllOption = false,
  allLabel = "All parties",
  disabled = false,
}) {
  const options = React.useMemo(() => {
    if (allowAllOption) return [{ id: "", name: allLabel }, ...parties];
    return parties;
  }, [parties, allowAllOption, allLabel]);

  const comboValue = React.useMemo(() => {
    if (allowAllOption && (value === "" || value === undefined || value === null)) return "";
    if (value === undefined || value === null || value === "") return undefined;
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }, [allowAllOption, value]);

  return (
    <SearchableSelect
      label={label}
      placeholder={placeholder}
      value={comboValue}
      onChange={(v) => {
        if (allowAllOption && (v === "" || v === null || v === undefined)) {
          onChange(undefined);
          return;
        }
        onChange(v ?? undefined);
      }}
      options={options}
      disabled={disabled}
      getLabel={(o) => o.name}
      getValue={(o) => o.id}
      overlayZClass="z-[100020]"
      menuZClass="z-[100021]"
    />
  );
}

/** Search + select for raw materials (same combobox as Party, not native `<select>`). */
function MaterialCombo({
  label = "Material",
  value,
  onChange,
  materials = [],
  disabled = false,
  placeholder = "Select material…",
}) {
  return (
    <SearchableSelect
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      options={materials}
      disabled={disabled}
      getLabel={(m) => m.name}
      getValue={(m) => m.id}
      overlayZClass="z-[100020]"
      menuZClass="z-[100021]"
    />
  );
}

export default function RmcConcreteQuotations() {
  const qc = useQueryClient();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [partyFilter, setPartyFilter] = React.useState("");
  const [activeOnly, setActiveOnly] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [quarryModalOpen, setQuarryModalOpen] = React.useState(false);
  const [quarryPartyId, setQuarryPartyId] = React.useState("");
  const [quarryMaterialId, setQuarryMaterialId] = React.useState("");
  const [newQuarryName, setNewQuarryName] = React.useState("");

  const { data: parties = [] } = useQuery({
    queryKey: ["purchaseParties"],
    queryFn: async () => (await rmcClient.listPurchaseParties({ is_active: true })).data,
  });

  const queryParams = React.useMemo(
    () => ({
      page,
      page_size: PAGE_SIZE,
      ...(partyFilter ? { party_id: partyFilter } : {}),
      ...(activeOnly ? { is_active: true } : {}),
    }),
    [page, partyFilter, activeOnly]
  );

  const { data: listData, isFetching } = useQuery({
    queryKey: ["concreteQuotations", queryParams],
    queryFn: async () => (await rmcClient.listConcreteQuotations(queryParams)).data,
    placeholderData: (prev) => prev,
  });

  const rows = React.useMemo(() => {
    if (!listData) return [];
    if (Array.isArray(listData?.results)) return listData.results;
    if (Array.isArray(listData?.data?.results)) return listData.data.results;
    if (Array.isArray(listData)) return listData;
    return [];
  }, [listData]);

  const totalCount = React.useMemo(() => {
    const v = Number(listData?.count ?? listData?.data?.count);
    return Number.isFinite(v) && v >= 0 ? v : rows.length;
  }, [listData, rows.length]);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(quotationSchema),
    defaultValues: {
      party_id: undefined,
      date: new Date().toISOString().slice(0, 10),
      valid_until: new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10),
      is_active: true,
      items: [{ material_id: undefined, quarry_id: undefined, rate: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "items" });
  const formPartyId = watch("party_id");

  const { data: materials = [] } = useQuery({
    queryKey: ["materials", formPartyId],
    queryFn: async () =>
      formPartyId ? (await rmcClient.listMaterials({ party_id: formPartyId })).data : [],
    enabled: Boolean(formPartyId),
  });

  const openCreate = () => {
    setEditingId(null);
    reset({
      party_id: undefined,
      date: new Date().toISOString().slice(0, 10),
      valid_until: new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10),
      is_active: true,
      items: [{ material_id: undefined, quarry_id: undefined, rate: "" }],
    });
    setDrawerOpen(true);
  };

  const openEdit = async (id) => {
    try {
      const raw = (await rmcClient.getConcreteQuotation(id)).data;
      const d = raw?.data ?? raw;
      const mappedItems = Array.isArray(d.items) && d.items.length
        ? d.items.map((it) => {
            const q = it.quarry ?? {};
            const mid = q.material_id ?? q.material?.id ?? it.material_id;
            return {
              material_id: mid ?? it.material_id,
              quarry_id: it.quarry_id ?? q.id,
              rate: it.rate != null ? String(it.rate) : "",
            };
          })
        : [{ material_id: undefined, quarry_id: undefined, rate: "" }];
      setEditingId(id);
      reset({
        party_id: d.party_id ?? d.party?.id,
        date: typeof d.date === "string" ? d.date.slice(0, 10) : "",
        valid_until: typeof d.valid_until === "string" ? d.valid_until.slice(0, 10) : "",
        is_active: d.is_active !== false,
        items: mappedItems,
      });
      setDrawerOpen(true);
    } catch {
      toast.error("Failed to load quotation");
    }
  };

  const save = useMutation({
    mutationFn: (payload) => {
      if (editingId) return rmcClient.updateConcreteQuotation(editingId, payload);
      return rmcClient.createConcreteQuotation(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["concreteQuotations"] });
      toast.success(editingId ? "Quotation updated" : "Quotation created");
      setDrawerOpen(false);
      setEditingId(null);
    },
    onError: (e) => toast.error(handleApiError(e)),
  });

  const onSubmit = (data) => {
    const itemsPayload = data.items.map((it) => ({
      quarry_id: Number(it.quarry_id),
      rate: roundMoney(Number(it.rate)).toFixed(2),
    }));
    const payload = {
      party_id: Number(data.party_id),
      date: data.date,
      valid_until: data.valid_until,
      items: itemsPayload,
    };
    if (Number.isFinite(Number(editingId)) && Number(editingId) > 0) {
      payload.is_active = data.is_active !== false;
    }
    save.mutate(payload);
  };

  const onSubmitInvalid = React.useCallback((errs) => {
    const firstMsg = (e) => {
      if (!e || typeof e !== "object") return null;
      if (typeof e.message === "string" && e.message) return e.message;
      if (Array.isArray(e)) {
        for (const item of e) {
          const m = firstMsg(item);
          if (m) return m;
        }
      }
      for (const v of Object.values(e)) {
        const m = firstMsg(v);
        if (m) return m;
      }
      return null;
    };
    toast.error(
      firstMsg(errs) || "Please fill all required fields (party, material, quarry, rate)."
    );
  }, []);

  const createQuarry = useMutation({
    mutationFn: () =>
      rmcClient.createPartyMaterialQuarry({
        party_id: Number(quarryPartyId),
        material_id: Number(quarryMaterialId),
        name: newQuarryName.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["partyMaterialQuarries"] });
      toast.success("Quarry added");
      setNewQuarryName("");
    },
    onError: () => toast.error("Failed to add quarry"),
  });

  const { data: quarryPartyMaterials = [] } = useQuery({
    queryKey: ["materials", quarryPartyId],
    queryFn: async () =>
      quarryPartyId
        ? (await rmcClient.listMaterials({ party_id: Number(quarryPartyId) })).data
        : [],
    enabled: quarryModalOpen && Boolean(quarryPartyId),
  });

  const { data: quarryModalQuarries = [] } = useQuery({
    queryKey: ["partyMaterialQuarries", quarryPartyId, quarryMaterialId],
    queryFn: async () =>
      (
        await rmcClient.listPartyMaterialQuarries({
          party_id: Number(quarryPartyId),
          material_id: Number(quarryMaterialId),
        })
      ).data,
    enabled:
      quarryModalOpen && Boolean(quarryPartyId) && Boolean(quarryMaterialId),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PageBreadCrumb pageTitle="Concrete quotations" />
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => setQuarryModalOpen(true)}>
            Party quarries
          </Button>
          <Button type="button" size="sm" onClick={openCreate}>
            New quotation
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200/80 bg-white/90 p-4 dark:border-gray-800 dark:bg-gray-900/50">
        <div className="mb-4 flex flex-wrap items-end gap-4">
          <div className="min-w-[220px]">
            <PurchasePartyCombo
              allowAllOption
              label="Party filter"
              placeholder="Search or select party…"
              allLabel="All parties"
              value={partyFilter}
              onChange={(v) => {
                setPartyFilter(v === undefined || v === null ? "" : String(v));
                setPage(1);
              }}
              parties={parties}
            />
          </div>
          <div className="flex items-center gap-4 pt-6">
            <Radio
              id="cq-active-y"
              name="cq-active"
              checked={activeOnly}
              onChange={() => {
                setActiveOnly(true);
                setPage(1);
              }}
              label="Active only"
            />
            <Radio
              id="cq-active-n"
              name="cq-active"
              checked={!activeOnly}
              onChange={() => {
                setActiveOnly(false);
                setPage(1);
              }}
              label="All"
            />
          </div>
        </div>

        <DataTable
          columns={[
            { key: "id", header: "ID" },
            {
              key: "party",
              header: "Party",
              render: (r) => partyLabel(r, parties),
            },
            { key: "date", header: "Date" },
            { key: "valid_until", header: "Valid until" },
            {
              key: "items",
              header: "Lines",
              render: (r) => (Array.isArray(r.items) ? r.items.length : "—"),
            },
            {
              key: "is_active",
              header: "Active",
              render: (r) => (r.is_active !== false ? "Yes" : "No"),
            },
            {
              key: "actions",
              header: "",
              headerClassName: "text-right",
              cellClassName: "text-right",
              render: (row) => (
                <button
                  type="button"
                  className="text-xs font-medium text-brand-500 hover:underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEdit(row.id);
                  }}
                >
                  Edit
                </button>
              ),
            },
          ]}
          data={rows}
          isLoading={isFetching}
        />
        <div className="mt-4 flex justify-end">
          <Pagination
            currentPage={Math.min(page, totalPages)}
            totalPages={totalPages}
            onPageChange={(p) => setPage(p)}
          />
        </div>
      </div>

      <DrawerPanel
        open={drawerOpen}
        onClose={() => {
          if (!save.isPending) {
            setDrawerOpen(false);
            setEditingId(null);
          }
        }}
        title={editingId ? "Edit concrete quotation" : "New concrete quotation"}
      >
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit, onSubmitInvalid)}>
          <Controller
            name="party_id"
            control={control}
            render={({ field }) => (
              <PurchasePartyCombo
                label="Party"
                placeholder="Select party…"
                value={field.value}
                onChange={(v) => {
                  field.onChange(v ?? undefined);
                  setValue("items", [{ material_id: undefined, quarry_id: undefined, rate: "" }]);
                }}
                parties={parties}
              />
            )}
          />
          {errors.party_id && (
            <p className="text-[11px] text-red-400">{errors.party_id.message}</p>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="cq-date">Date</Label>
              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <Input
                    id="cq-date"
                    type="date"
                    className="mt-1"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />
              {errors.date && (
                <p className="mt-1 text-[11px] text-red-400">{errors.date.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="cq-valid">Valid until</Label>
              <Controller
                name="valid_until"
                control={control}
                render={({ field }) => (
                  <Input
                    id="cq-valid"
                    type="date"
                    className="mt-1"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />
              {errors.valid_until && (
                <p className="mt-1 text-[11px] text-red-400">{errors.valid_until.message}</p>
              )}
            </div>
          </div>
          <Controller
            name="is_active"
            control={control}
            render={({ field }) => (
              <div className="flex items-center gap-6">
                <Radio
                  id="q-act"
                  name="q-is-active"
                  checked={field.value === true}
                  onChange={() => field.onChange(true)}
                  label="Active"
                />
                <Radio
                  id="q-inact"
                  name="q-is-active"
                  checked={field.value === false}
                  onChange={() => field.onChange(false)}
                  label="Inactive"
                />
              </div>
            )}
          />

          <div className="space-y-3 border-t border-gray-800 pt-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Lines (quarry & rate)
            </p>
            {fields.map((fld, idx) => (
              <QuotationLine
                key={fld.id}
                idx={idx}
                control={control}
                watch={watch}
                setValue={setValue}
                errors={errors}
                materials={materials}
                partyId={formPartyId}
                remove={() => remove(idx)}
                canRemove={fields.length > 1}
              />
            ))}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => append({ material_id: undefined, quarry_id: undefined, rate: "" })}
            >
              Add line
            </Button>
            {errors.items?.message && (
              <p className="text-[11px] text-red-400">{errors.items.message}</p>
            )}
          </div>

          <Button type="submit" size="sm" disabled={save.isPending}>
            {save.isPending ? "Saving…" : editingId ? "Update" : "Create"}
          </Button>
        </form>
      </DrawerPanel>

      <Modal isOpen={quarryModalOpen} onClose={() => setQuarryModalOpen(false)} className="max-w-lg w-full">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Party material quarries</h3>
          <p className="text-xs text-gray-400">
            Quarries are scoped to a purchase party and one raw material. Use them on concrete quotations and
            concrete purchase challans.
          </p>
          <PurchasePartyCombo
            label="Party"
            placeholder="Select party…"
            value={quarryPartyId === "" ? undefined : Number(quarryPartyId)}
            onChange={(v) => {
              setQuarryPartyId(v === undefined || v === null ? "" : String(v));
              setQuarryMaterialId("");
            }}
            parties={parties}
          />
          <MaterialCombo
            label="Material"
            placeholder="Select material…"
            value={quarryMaterialId === "" ? undefined : Number(quarryMaterialId)}
            onChange={(v) => setQuarryMaterialId(v != null ? String(v) : "")}
            materials={quarryPartyMaterials}
            disabled={!quarryPartyId}
          />
          <div>
            <Label>New quarry name</Label>
            <Input
              value={newQuarryName}
              onChange={(e) => setNewQuarryName(e.target.value)}
              className="mt-1"
              placeholder="e.g. North Quarry"
            />
          </div>
          <Button
            type="button"
            size="sm"
            disabled={
              !quarryPartyId ||
              !quarryMaterialId ||
              !newQuarryName.trim() ||
              createQuarry.isPending
            }
            onClick={() => createQuarry.mutate()}
          >
            {createQuarry.isPending ? "Adding…" : "Add quarry"}
          </Button>
          {quarryPartyId && quarryMaterialId && (
            <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-800">
              <table className="w-full text-left text-xs">
                <thead className="sticky top-0 bg-gray-950">
                  <tr>
                    <th className="px-3 py-2 font-semibold text-gray-500">ID</th>
                    <th className="px-3 py-2 font-semibold text-gray-500">Name</th>
                  </tr>
                </thead>
                <tbody>
                  {quarryModalQuarries.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="px-3 py-4 text-gray-500">
                        No quarries yet for this party and material.
                      </td>
                    </tr>
                  ) : (
                    quarryModalQuarries.map((q) => (
                      <tr key={q.id} className="border-t border-gray-800">
                        <td className="px-3 py-2 font-mono text-gray-300">{q.id}</td>
                        <td className="px-3 py-2 text-gray-200">{q.name}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex justify-end border-t border-gray-800 pt-4">
            <Button type="button" variant="outline" size="sm" onClick={() => setQuarryModalOpen(false)}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function QuotationLine({
  idx,
  control,
  watch,
  setValue,
  errors,
  materials,
  partyId,
  remove,
  canRemove,
}) {
  const mid = watch(`items.${idx}.material_id`);
  const quarryIdWatch = watch(`items.${idx}.quarry_id`);
  const { data: quarries = [] } = useQuery({
    queryKey: ["partyMaterialQuarries", partyId, mid],
    queryFn: async () =>
      (await rmcClient.listPartyMaterialQuarries({ party_id: partyId, material_id: mid })).data,
    enabled: Boolean(partyId && mid),
  });

  React.useEffect(() => {
    if (quarryIdWatch == null) return;
    if (!quarries.some((q) => Number(q.id) === Number(quarryIdWatch))) {
      setValue(`items.${idx}.quarry_id`, undefined, { shouldValidate: true });
    }
  }, [quarries, quarryIdWatch, idx, setValue]);

  return (
    <div className="rounded-lg border border-gray-800 p-3 space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Controller
          name={`items.${idx}.material_id`}
          control={control}
          render={({ field }) => (
            <MaterialCombo
              label="Material"
              placeholder="Select material…"
              value={field.value}
              onChange={(v) => {
                field.onChange(v ?? undefined);
                setValue(`items.${idx}.quarry_id`, undefined);
              }}
              materials={materials}
              disabled={!partyId}
            />
          )}
        />
        <Controller
          name={`items.${idx}.quarry_id`}
          control={control}
          render={({ field }) => (
            <SearchableSelect
              label="Quarry"
              placeholder="Select quarry…"
              value={field.value}
              onChange={field.onChange}
              options={quarries}
              disabled={!mid}
              getLabel={(q) => q.name || `#${q.id}`}
              getValue={(q) => q.id}
              overlayZClass="z-[100020]"
              menuZClass="z-[100021]"
            />
          )}
        />
        <div>
          <Label>Rate (₹)</Label>
          <Controller
            name={`items.${idx}.rate`}
            control={control}
            render={({ field }) => (
              <Input
                type="number"
                step="0.01"
                className="mt-1"
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
            )}
          />
        </div>
      </div>
      {errors.items?.[idx] && (
        <p className="text-[11px] text-red-400">
          {errors.items[idx]?.material_id?.message ||
            errors.items[idx]?.quarry_id?.message ||
            errors.items[idx]?.rate?.message}
        </p>
      )}
      {canRemove && (
        <div className="flex justify-end">
          <button type="button" className="text-xs text-red-400 hover:underline" onClick={remove}>
            Remove line
          </button>
        </div>
      )}
    </div>
  );
}
